'use client';

import { useState } from 'react';
import { Lead } from '../../../../types/lead';
import { trackEvent } from '../../../google-tag-manager';
import { useLeadCapture } from '../../../use-lead-capture';
import { useCampaignTracker } from '../../campaign-tracker';

const BlackFridayForm: React.FC<{
  onSubmitted: () => void;
}> = (props) => {
  const { onSubmitted } = props;

  const leadCapture = useLeadCapture({ persist: false });

  const trackingInfo = useCampaignTracker();

  const [state, setState] = useState<{
    loading: boolean;
    error: boolean;
    details: {
      name: string;
      email: string;
      mobile: string;
      teamSize?: Lead['details']['teamSize'];
    },
  }>({
    loading: false,
    error: false,
    details: {
      name: '',
      email: '',
      mobile: '',
    },
  });

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (state.loading) { return; }

    setState((s) => ({ ...s, loading: true }));

    const [firstName, lastName] = state.details.name.split(' ');
    const leadDetails = {
      firstName,
      lastName,
      email: state.details.email,
      mobile: state.details.mobile,
      teamSize: state.details.teamSize,
    };

    void leadCapture.submit({
      type: 'black-friday',
      details: leadDetails,
      tracking: trackingInfo,
    }).then(() => {
      trackEvent({
        event: 'get_black_friday_deal',
      });

      onSubmitted();
    }, () => {
      setState((s) => ({ ...s, error: true }));
    }).finally(() => {
      setState((s) => ({ ...s, loading: false }));
    });
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (state.loading) { return; }

    const { name, value } = event.currentTarget;

    switch (name) {
      case 'name':
      case 'email':
      case 'mobile':
      case 'teamSize': break;
      default: return;
    }

    setState((s) => ({
      ...s,
      details: {
        ...s.details,
        [name]: value,
      },
    }));
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-2">
        <div className="form-control">
          <label className="label">
            <span className="label-text font-medium">
              Name
            </span>
          </label>
          <input
            required
            autoComplete="name"
            className="input input-bordered"
            value={state.details.name}
            onChange={handleChange}
            name="name"
            readOnly={state.loading}
          />
        </div>

        <div className="form-control">
          <label className="label">
            <span className="label-text font-medium">
              Email
            </span>
          </label>
          <input
            required
            type="email"
            autoComplete="email"
            className="input input-bordered"
            value={state.details.email}
            onChange={handleChange}
            name="email"
            readOnly={state.loading}
          />
        </div>

        <div className="form-control">
          <label className="label">
            <span className="label-text font-medium">
              Mobile
            </span>
          </label>
          <input
            required
            type="tel"
            autoComplete="tel"
            className="input input-bordered"
            value={state.details.mobile}
            onChange={handleChange}
            name="mobile"
            readOnly={state.loading}
          />
        </div>

        <div className="form-control">
          <label className="label">
            <span className="label-text font-medium">
              Team size
            </span>
          </label>
          <input
            type="number"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="off"
            className="input input-bordered"
            value={state.details.teamSize}
            onChange={handleChange}
            name="teamSize"
            min={1}
            step={1}
            readOnly={state.loading}
          />
        </div>
      </div>

      <div className="mt-6">
        <button
          className={`btn btn-primary text-lg btn-block md:btn-lg ${state.loading ? 'loading' : ''}`}
          type="submit"
        >
          Submit
        </button>

        {state.error ? (
          <div className="alert alert-error mt-2">
            Oops! Something went wrong.
          </div>
        ) : null}
      </div>
    </form>
  );
};

export default BlackFridayForm;
